'use client';

import clsx from 'classnames';
import Hamburger from 'icons/lib/hamburger_mini.svg';
import MobileSearch from 'icons/lib/magnifier_mobile1.svg';
import NavBasket from 'icons/lib/nav_basket.svg';
import User from 'icons/lib/nav_offers_user.svg';
import Link from 'next/link';
import { MouseEvent, useEffect, useMemo, useRef } from 'react';
import { useWindowDimensions } from 'ui';

import { useTranslation } from '@/i18n/client';
import { Autocomplete } from '@/modules/google/retail/complete-query/components/autocomplete/autocomplete';
import { Logo } from '@/shared/components/logo';
import { useAuthentication } from '@/shared/context/authentication-context';
import { useBasket } from '@/shared/context/basket-context';
import { routes } from '@/shared/data/routes';
import { useQuerySearchParam } from '@/shared/hooks/use-query-search-param';
import { useCurrentLocale } from '@/shared/utils/client';

import styles from './HeaderTopBar.module.scss';
import { HeaderTopBarProps } from './types';

export function HeaderTopBar({
  topMenu,
  onCartPress,
  onHamburgerPress,
  onHamburgerHover,
  onAccountPress,
}: HeaderTopBarProps) {
  const { updateQuery } = useQuerySearchParam();
  const textFieldSearchRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('header');
  const headerInfoContainer = useRef<HTMLDivElement>(null);
  const headerMainContainer = useRef<HTMLDivElement>(null);
  const locale = useCurrentLocale();
  const { user } = useAuthentication();

  const { width } = useWindowDimensions();
  const { miniBasket, freeShippingPrice } = useBasket();
  let lastScrollTop = 0;

  const onHamburgerIconClick = (event: MouseEvent) => {
    event.preventDefault();
    onHamburgerPress?.();
  };

  const onCartIconClick = (event: MouseEvent) => {
    event.preventDefault();
    if (!miniBasket?.items?.length) return;
    onCartPress?.();
  };

  const onAccountIconPress = (event: MouseEvent) => {
    if (!user) {
      event.preventDefault();
      onAccountPress?.();
      return;
    }
  };

  const onMobileSearchClick = (event: MouseEvent) => {
    event.preventDefault();
    textFieldSearchRef.current?.focus();
  };

  const scrollChange = () => {
    if (!headerInfoContainer.current || !headerMainContainer.current) {
      return;
    }

    const scrollTop = window.pageYOffset;

    if (width < 768) {
      headerMainContainer.current.style.transform = 'translateY(0%)';
      headerMainContainer.current.style.position = 'initial';
      headerMainContainer.current.style.transition = 'none';
      headerMainContainer.current.style.boxShadow = 'none';
      headerInfoContainer.current.style.marginBottom = '0px';
      return;
    }

    if (Math.abs(lastScrollTop - scrollTop) <= 5) return;

    if (scrollTop < headerInfoContainer.current.clientHeight) {
      headerMainContainer.current.style.transform = 'translateY(0%)';
      headerMainContainer.current.style.position = 'initial';
      headerMainContainer.current.style.transition = 'none';
      headerMainContainer.current.style.boxShadow = 'none';
      headerInfoContainer.current.style.marginBottom = '0px';

      lastScrollTop = scrollTop;
      return;
    }

    if (scrollTop > lastScrollTop) {
      headerMainContainer.current.style.transform = 'translateY(0%)';
      headerMainContainer.current.style.transition = 'transform 0.5s';
      headerMainContainer.current.style.boxShadow = 'none';

      lastScrollTop = scrollTop;
      return;
    }

    if (lastScrollTop > scrollTop) {
      headerInfoContainer.current.style.marginBottom = `${headerMainContainer.current.clientHeight}px`;
      headerMainContainer.current.style.position = 'fixed';
      headerMainContainer.current.style.top = `-${headerMainContainer.current.clientHeight}px`;
      headerMainContainer.current.style.transform = 'translateY(100%)';
      headerMainContainer.current.style.zIndex = '10';
      headerMainContainer.current.style.transition = 'transform 0.5s';
      headerMainContainer.current.style.boxShadow =
        '2.4px 3.2px 6px 0 rgba(0, 0, 0, 0.1)';
      lastScrollTop = scrollTop;
      return;
    }
  };

  useEffect(() => {
    const watchScroll = () => {
      window.addEventListener('scroll', scrollChange);
    };
    watchScroll();
    return () => {
      window.removeEventListener('scroll', scrollChange);
    };
  }, [width]);

  const productsCount = useMemo(() => {
    return (miniBasket?.items || []).reduce((count, item) => {
      return count + item.quantity;
    }, 0);
  }, [miniBasket?.items]);

  return (
    <div className={clsx(styles.headerWrapper, 'header')}>
      <div
        ref={headerInfoContainer}
        className={clsx(styles.headerInfoContainer, 'top-section')}
      >
        <div className={styles.content}>
          <div className="container">
            <p>{t('topHeader.immediateShipment')}</p>
            <p>{t('topHeader.30days')}</p>
            <Link prefetch={false} href={routes[locale].CONTACT}>
              {t('topHeader.helpAndContact')}
            </Link>
          </div>
        </div>
      </div>

      <div
        className={styles.headerMainContainerWrapper}
        ref={headerMainContainer}
      >
        <div className={styles.headerMainContainer}>
          <div className="container">
            <div className={styles.iconContainer}>
              <Link
                prefetch={false}
                href="#"
                onClick={onHamburgerIconClick}
                onMouseEnter={onHamburgerHover}
              >
                <Hamburger />
              </Link>
              <Link
                prefetch={false}
                href="#"
                onClick={onMobileSearchClick}
                onMouseEnter={onHamburgerHover}
                className={styles.mobileSearch}
              >
                <MobileSearch />
              </Link>
            </div>
            <div className={styles.mainSection}>
              <div>
                <Link prefetch={false} className={styles.logoWrapper} href="/">
                  <Logo />
                </Link>
              </div>

              <Autocomplete textFieldRef={textFieldSearchRef} />
            </div>

            <div className={styles.iconsContainer}>
              <Link
                prefetch={false}
                href={routes[locale].ACCOUNT_ORDERS}
                onClick={onAccountIconPress}
              >
                <User className={styles.userIcon} />
                <span className={styles.iconLabel}>
                  {t('middleHeader.account')}
                </span>
              </Link>

              <Link
                prefetch={false}
                href={routes[locale].CART}
                onClick={onCartIconClick}
                style={{
                  cursor: miniBasket?.items?.length ? 'pointer' : 'default',
                }}
              >
                <NavBasket />
                <span className={styles.iconLabel}>
                  {t('middleHeader.cart')}
                </span>
                <span className={styles.itemsCounter}>{productsCount}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <nav className={styles.headerLinkContainer}>
        <div className="container">
          <ul className={styles.content}>
            {!!topMenu?.content?.length &&
              topMenu.content?.map((link, index) => (
                <Link prefetch={false} key={index} href={link?.url ?? '#'}>
                  {link?.label}
                </Link>
              ))}
          </ul>
        </div>
      </nav>
    </div>
  );
}
